import { useState } from "react";
import { motion } from "framer-motion";
import { Send, Mail } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Message envoyé", { description: "Nous vous répondrons sous 24h ouvrées." });
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error("Envoi impossible", {
        description: typeof detail === "string" ? detail : "Réessayez dans un instant.",
      });
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full bg-ink border border-white/15 px-4 py-3.5 text-sm focus:outline-none focus:border-volt placeholder:text-ash transition-colors";

  return (
    <main className="pt-16 min-h-screen grain relative" data-testid="contact-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-2 gap-16">
        <Reveal>
          <p className="font-mono text-[11px] tracking-[0.3em] uppercase text-blood mb-4">[ Contact ]</p>
          <h1 className="font-syne font-extrabold uppercase tracking-tighter text-5xl sm:text-6xl leading-none mb-8">
            Parlez-<br />nous<span className="text-blood">.</span>
          </h1>
          <p className="text-smoke leading-relaxed max-w-md mb-10">
            Une question sur une taille, une commande, un drop à venir ?
            Écrivez-nous — un humain (pas un robot) vous répond sous 24h ouvrées.
          </p>
          <a
            href="mailto:twelveshop669contact@gmail.com"
            data-testid="contact-direct-email"
            className="inline-flex items-center gap-3 border border-white/15 px-5 py-3.5 font-mono text-xs tracking-[0.1em] hover:border-volt hover:text-volt transition-colors"
          >
            <Mail size={15} />
            twelveshop669contact@gmail.com
          </a>
        </Reveal>

        <Reveal delay={0.15}>
          <form data-testid="contact-form" onSubmit={submit} className="border border-white/10 bg-coal p-8 space-y-5">
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-ash block mb-2">Votre nom</label>
              <input
                data-testid="contact-name-input"
                required
                minLength={2}
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Jean Dupont"
                className={inputCls}
              />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-ash block mb-2">Votre e-mail</label>
              <input
                data-testid="contact-email-input"
                type="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="jean@exemple.fr"
                className={inputCls}
              />
            </div>
            <div>
              <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-ash block mb-2">Votre message</label>
              <textarea
                data-testid="contact-message-input"
                required
                minLength={10}
                rows={6}
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                placeholder="Bonjour, je voudrais savoir si…"
                className={`${inputCls} resize-none`}
              />
            </div>
            <button
              data-testid="contact-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-blood text-white font-mono text-xs uppercase tracking-[0.2em] py-4 flex items-center justify-center gap-3 hover:bg-white hover:text-ink transition-colors disabled:opacity-50"
            >
              {loading ? "Envoi en cours…" : "Envoyer le message"}
              {!loading && <Send size={14} />}
            </button>
            <p className="text-center font-mono text-[10px] text-ash uppercase tracking-[0.15em]">
              Réponse sous 24h ouvrées
            </p>
          </form>
        </Reveal>
      </div>
      <Footer />
    </main>
  );
}
